/* eslint-disable no-restricted-imports */
import type { BeforeRequestHook } from 'ky';

import { useAuthStore } from '~/store/auth.store';
import { requestSubsidiaryId } from '~/utils/access-token';

/**
 * Ky beforeRequest hook — injects X-Subsidiary-ID scoped to the SAME
 * subsidiary the Bearer access token is issued for.
 *
 * The header is sourced from the access token's `subsidiaryId` claim, not from
 * `user.activeTenantId`. Those two are hydrated independently (token from
 * storage, `activeTenantId` from `/auth/me`, which the backend resolves off
 * this very header), so with per-tab scoped tokens they can drift apart and
 * point at different subsidiaries — and identity-service's UserSubsidiaryGuard
 * 403s any request whose token subsidiary ≠ request subsidiary. Deriving the
 * header from the token makes the two agree by construction. Falls back to
 * `activeTenantId` only if the token can't be decoded.
 *
 * A caller MAY set `X-Subsidiary-ID` explicitly (e.g. the Integrations pages
 * scope their own requests to a picked subsidiary without changing the tab's
 * token). When a request already carries the header, this hook leaves it
 * untouched — the explicit page-local override wins over the token default.
 *
 * Uses `.getState()` (static) — never the React hook.
 */
export const subsidiaryInterceptor: BeforeRequestHook = (request) => {
  if (request.headers.has('X-Subsidiary-ID')) return;
  const tenantId = requestSubsidiaryId(useAuthStore.getState());
  if (tenantId) {
    request.headers.set('X-Subsidiary-ID', tenantId);
  }
};
